import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import {
    cancelFuelSaving as libraryCancelFuelSaving,
    fetchFuelEconomy,
    type FuelSavingApplyPayload,
    type FetchFuelEconomyPayload,
    type FuelEconomyData
} from '@makemydeal/dr-activities-fuel-saving';
import { setBaggageFuelEconomy } from '@makemydeal/dr-dash-store/actions/baggageFuelEconomyActionCreators';
import { mergeFuelEconomyFromApi } from '@makemydeal/dr-dash-store/actions/fuelEconomyActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as printPdfActionCreator from '@makemydeal/dr-dash-store/actions/printPdfActionCreator';
import { scopedActions, scopedStateSelectors } from '@makemydeal/shared-scoped-store';

import { buildBaggageFuelEconomyPayload } from './utils/buildBaggageFuelEconomyPayload';
import { buildFuelEconomyMergePayload } from './utils/buildFuelEconomyMergePayload';
import { getScopeVehicleVin, toSuccessfulApplyPayload, getVehiclesToUpdate } from './utils/applyPayloadUtils';
import { FUEL_SAVING_APPLY_SOURCE, FUEL_SAVING_APPLY_AND_PRINT_SOURCE } from './constants';
import type {
    ThunkDispatch,
    PrimaryScopeSelectorState,
    InitializedScopesSelectorState,
    ScopeByIdSelectorState,
    CurrentScopeSelectorState,
    FuelEconomyResponse,
    ScopeStateWithVehicle
} from './types';

/**
 * Applies baggage and per-scope fuel economy state updates for all vehicles in the payload.
 * Shared by both applyFuelSavingThunk and applyAndPrintFuelSaving so their state-update
 * logic stays in sync.
 */
const dispatchFuelEconomyUpdates = (payload: FuelSavingApplyPayload, dispatch: ThunkDispatch, getState: () => unknown): void => {
    const successfulPayload = toSuccessfulApplyPayload(payload);
    dispatch(setBaggageFuelEconomy(buildBaggageFuelEconomyPayload(successfulPayload)) as AnyFSA);

    const state = getState();
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state as PrimaryScopeSelectorState);
    const orderedScopeIds = scopedStateSelectors.getInitializedScopesOrder(state as InitializedScopesSelectorState);
    const scopeIdByVin = new Map<string, string>();

    for (const scopeId of orderedScopeIds) {
        const scopeState = scopedStateSelectors.getScopeStateById(state as ScopeByIdSelectorState, scopeId) as
            | ScopeStateWithVehicle
            | undefined;
        const scopeVin = getScopeVehicleVin(scopeState);
        if (scopeVin) {
            scopeIdByVin.set(scopeVin, scopeId);
        }
    }

    const successfulVehicles = getVehiclesToUpdate(successfulPayload);
    for (const vehicleEntry of successfulVehicles) {
        const targetScopeId = vehicleEntry.vehicle.isPrimary
            ? primaryScopeId
            : scopeIdByVin.get(vehicleEntry.vehicle.id) || primaryScopeId;

        const fuelEconomyAction = mergeFuelEconomyFromApi(
            buildFuelEconomyMergePayload({ vehicle: vehicleEntry.vehicle, trades: vehicleEntry.trades })
        );
        const scopedFuelEconomyAction = scopedActions.createScopedAction(targetScopeId, fuelEconomyAction);
        dispatch(scopedFuelEconomyAction as AnyFSA);
    }
};

const applyFuelSavingThunk =
    (payload: FuelSavingApplyPayload, source: string) => (dispatch: ThunkDispatch, getState: () => unknown) => {
        dispatchFuelEconomyUpdates(payload, dispatch, getState);
        dispatch(offerActionCreators.saveOffer({ source }) as AnyFSA);
    };

export const hostActions = {
    applyFuelSaving: (payload: FuelSavingApplyPayload) => {
        return applyFuelSavingThunk(payload, FUEL_SAVING_APPLY_SOURCE);
    },

    applyAndPrintFuelSaving: (payload: FuelSavingApplyPayload) => {
        return (dispatch: ThunkDispatch, getState: () => unknown) => {
            // Apply baggage/fuel-economy state updates without calling saveOffer —
            // callGeneratePdfService (triggered by generatePdf) calls saveOffer({ isDraft: true })
            // itself, so dispatching applyFuelSavingThunk would cause a duplicate save.
            dispatchFuelEconomyUpdates(payload, dispatch, getState);
            dispatch(printPdfActionCreator.generatePdf('fuelSaving') as AnyFSA);
        };
    },

    persistFuelSavingValues: (payload: FuelSavingApplyPayload) => {
        return (dispatch: ThunkDispatch) => {
            dispatch(setBaggageFuelEconomy(buildBaggageFuelEconomyPayload(payload)) as AnyFSA);
        };
    },

    cancelFuelSaving: () => {
        return (dispatch: ThunkDispatch) => {
            dispatch(libraryCancelFuelSaving() as AnyFSA);
        };
    },

    fetchFuelEconomyData: (payload: FetchFuelEconomyPayload) => {
        return async (dispatch: ThunkDispatch, getState: () => unknown): Promise<FuelEconomyData | null> => {
            const rsaaResult = await (dispatch as unknown as (action: unknown) => Promise<FuelEconomyResponse>)(
                fetchFuelEconomy(payload)
            );

            if (!rsaaResult || rsaaResult.error) return null;

            const fuelEconomyData = rsaaResult.payload;

            if (fuelEconomyData?.ok && fuelEconomyData.data) {
                const mergeAction = mergeFuelEconomyFromApi({
                    sellingVehicles: fuelEconomyData.data.sellingVehiclesVin || [],
                    tradeInVehicles: fuelEconomyData.data.tradeinVehiclesVin || []
                });

                const state = getState();
                const currentScopeId = scopedStateSelectors.getCurrentScopeId(state as CurrentScopeSelectorState);
                if (typeof currentScopeId === 'string' && currentScopeId.length > 0) {
                    dispatch(scopedActions.createScopedAction(currentScopeId, mergeAction) as AnyFSA);
                } else {
                    dispatch(mergeAction as AnyFSA);
                }
            }

            return fuelEconomyData ?? null;
        };
    }
};
